import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./styles.module.scss";
import Page from "../../Components/Page";
import PageTitle from "../../Components/PageTitle";
import InsightCard from "../../Components/InsightCard";
import DatePresetSelector from "../../Components/DatePresetSelector";
import InstallPerformanceChart from "../../Components/InstallPerformanceChart";
import InstallRecordsTable from "../../Components/InstallRecordsTable";
import CircularProgress from "@mui/material/CircularProgress";
import PrimaryButton from "../../Components/PrimaryButton";
import { useGetUserRole } from "../../Services/Hooks";
import { useInstallPerformance } from "../../Services/Hooks/useInstallPerformance";
import { useAmplitudeTracking } from "Services/Hooks/useAmplitudeTracking";
import { useAppSelector } from "State/hooks";
import {
  getPresetDateRange,
  flattenAllRecords,
  formatRangeLabel,
  PRESETS,
} from "./utils";
import type { PresetKey } from "./utils";
import type { Granularity } from "@intelligentlilli/api-layer";

type TabKey = "summary" | "history";

const InstallPerformance = () => {
  const navigate = useNavigate();
  const { userIsManagerOrAdmin, userRoleAsAString } = useGetUserRole();
  const user = useAppSelector((state) => state.user.authData);
  const orgId = user?.organisationId;

  // Which folder tab is active
  const [activeTab, setActiveTab] = useState<TabKey>("summary");
  const track = useAmplitudeTracking();

  const handleTabClick = (tab: TabKey) => {
    setActiveTab(tab);
    track("Install Performance", {
      action: "tab_selected",
      tab,
    });
  };

  // Org 103 can only view History as an admin; every other org can view it
  // as a manager or admin.
  const canViewHistory =
    orgId === 103 ? userRoleAsAString === "Admin" : userIsManagerOrAdmin;

  // Role guard — redirect non-managers/admins
  useEffect(() => {
    if (!userIsManagerOrAdmin) {
      navigate("/");
    }
  }, [userIsManagerOrAdmin, navigate]);

  // Date controls state
  const defaultPreset =
    PRESETS.find((p) => p.key === "last4weeks") ?? PRESETS[0];
  const [preset, setPreset] = useState<PresetKey>(defaultPreset.key);
  const [granularity, setGranularity] = useState<Granularity>(
    defaultPreset.defaultGranularity
  );
  // Compute dates from preset
  const { startTime, endTime } = useMemo(
    () => getPresetDateRange(preset),
    [preset]
  );

  // Fetch data
  const { data, loading, error, refetch } = useInstallPerformance(
    orgId,
    granularity,
    startTime,
    endTime
  );

  /* 
    Current snapshot
  */
  // taken directly from API
  const kitsInstalled =
    data?.currentSnapshot?.kits?.inUseActiveServiceUsers ?? 0;
  const totalKits = data?.currentSnapshot?.kits?.count ?? 0;
  // Derived values from current snapshot
  const kitsAvailable = totalKits - kitsInstalled;

  /* 
    All time summary - taken directly from API
  */
  const totalReferrals = data?.allTimeSummary?.referredServiceUsers ?? 0;
  const allTimeSuccessfulInstalls = data?.allTimeSummary?.successfulInstalls ?? 0;
  // Derived values from All time summary
  const utilisationPct =
    totalKits > 0 ? Math.round((kitsInstalled / totalKits) * 100) : 0;

  // All install records (successful + unsuccessful) for the combined table
  const allRecords = useMemo(
    () => flattenAllRecords(data?.periods ?? []),
    [data]
  );

  // Residents' *current* install status, joined from the globally-fetched
  // serviceUsers state (the install-summary API only has historical events).
  const serviceUsers = useAppSelector((state) => state.serviceUsers);
  const installStatusById = useMemo(() => {
    const map = new Map<number, string>();
    (serviceUsers ?? []).forEach((su: any) => {
      if (su?.id != null) {
        map.set(
          su.id,
          su?.serviceUserProperties?.installationStatus || "incomplete"
        );
      }
    });
    return map;
  }, [serviceUsers]);

  // Organisation has no kits and no recorded activity at all
  const hasPeriodActivity = (data?.periods ?? []).some(
    (p) =>
      p.metrics.startedCount +
        p.metrics.referredCount +
        p.metrics.failedCount +
        p.metrics.succeededCount >
      0
  );
  const isEmptyOrg =
    Boolean(data) &&
    totalKits === 0 &&
    totalReferrals === 0 &&
    allTimeSuccessfulInstalls === 0 &&
    !hasPeriodActivity;

  if (!userIsManagerOrAdmin) {
    return null;
  }

  return (
    <Page className={styles.page}>
      <div className={styles.content}>
        <div className={styles.sticky_header}>
          <PageTitle title="Install performance" />
        </div>

        {loading && !data && (
          <div className={styles.loading}>
            <CircularProgress size={40} />
          </div>
        )}

        {error && !data && (
          <div className={styles.error}>
            <p>Failed to load install performance data.</p>
            <PrimaryButton onClick={refetch}>Retry</PrimaryButton>
          </div>
        )}

        {!data && !loading && !error && (
          <div className={styles.placeholder}>
            <h2>No data available</h2>
            <p>
              We couldn't load install performance data for your organisation.
            </p>
            <PrimaryButton onClick={refetch}>Retry</PrimaryButton>
          </div>
        )}

        {error && data && (
          <div className={styles.stale_warning}>
            <p>Unable to refresh data. Showing cached results.</p>
            <PrimaryButton onClick={refetch}>Retry</PrimaryButton>
          </div>
        )}

        {data && isEmptyOrg && (
          <div className={styles.placeholder}>
            <h2>No installation data yet</h2>
            <p>
              Nothing has been recorded for your organisation so far. Data will
              appear here once kits are allocated and installs begin.
            </p>
          </div>
        )}

        {data && !isEmptyOrg && (
          <>
            {/* Folder tabs */}
            <div className={styles.tabs} role="tablist">
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === "summary"}
                className={`${styles.tab} ${activeTab === "summary" ? styles.tab_active : ""}`}
                onClick={() => handleTabClick("summary")}
              >
                Summary
              </button>
              {canViewHistory && (
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeTab === "history"}
                  className={`${styles.tab} ${activeTab === "history" ? styles.tab_active : ""}`}
                  onClick={() => handleTabClick("history")}
                >
                  History
                </button>
              )}
            </div>

            <div className={styles.tab_panel}>
              {activeTab === "summary" && (
                <>
                  {/* Current snapshot */}
                  <section>
                    <h2 className={styles.section_title}>Current snapshot</h2>
                    <div className={styles.cards_grid}>
                      <InsightCard
                        title="Kits currently installed"
                        primaryMetric={`${kitsInstalled}/${totalKits}`}
                        supportingText={
                          `${kitsInstalled} kits successfully installed against ${totalKits} contracted kits, leaving ${kitsAvailable} of the contracted allocation unused \u2014 ${utilisationPct}% utilisation rate.`
                        }
                        progressBar={{
                          current: kitsInstalled,
                          total: totalKits,
                        }}
                      />
                      <InsightCard
                        title="Kits available for deployment"
                        primaryMetric={kitsAvailable}
                        supportingText={
                          `${kitsAvailable} kit${kitsAvailable !== 1 ? "s are" : " is"} not being used and ${kitsAvailable !== 1 ? "are" : "is"}
                          available to install in new homes.`
                        }
                      />
                    </div>
                  </section>

                  {/* All-time performance */}
                  <section>
                    <h2 className={styles.section_title}>All-time performance</h2>
                    <div className={styles.cards_grid}>
                      <InsightCard
                        title="Total residents created on the platform to date"
                        primaryMetric={totalReferrals}
                        supportingText="Residents created on the platform across the lifetime of the contract."
                      />
                      <InsightCard
                        title="Successful installs to date"
                        primaryMetric={allTimeSuccessfulInstalls}
                        supportingText={`${allTimeSuccessfulInstalls} successful installs so far.`}
                      />
                    </div>
                  </section>
                </>
              )}

              {activeTab === "history" && canViewHistory && (
                <>
                  {/* Date controls — set the time horizon for the chart and table */}
                  <div className={styles.chart_controls}>
                    <DatePresetSelector
                      preset={preset}
                      setPreset={setPreset}
                      granularity={granularity}
                      setGranularity={setGranularity}
                    />
                    <p className={styles.range_label}>
                      Showing{" "}
                      {formatRangeLabel({ start: startTime, end: endTime }, granularity)}
                    </p>
                  </div>

                  {/* Chart */}
                  <InstallPerformanceChart
                    periods={data.periods}
                    granularity={granularity}
                    range={data.metadata?.range}
                    timezone={data.metadata?.timezone}
                  />

                  {/* All install records, successful and unsuccessful */}
                  <InstallRecordsTable
                    records={allRecords}
                    range={data.metadata?.range}
                    granularity={granularity}
                    installStatusById={installStatusById}
                  />
                </>
              )}
            </div>
          </>
        )}
      </div>
    </Page>
  );
};

export default InstallPerformance;
